/* iter-121.126 — EV (Entity Viewer) Home cockpit.
 *
 * Single consolidated widget rendering the redesigned EV Home as two
 * perspectives: "For Your Action" (5 cols) + "For Your Information" (7
 * cols) on lg+, stacked on smaller viewports. Backed by
 * /api/home/ev/cockpit (one round-trip for KPIs + actions + info).
 *
 * Design language: Swiss/high-contrast (per /app/design_guidelines.json).
 *   • Flat 1px borders, no shadows.
 *   • Crisp KPI numbers (font-medium, large).
 *   • Asymmetric 5/7 split — Action column gets the primary attention.
 *   • Action items get a subtle red left-border accent.
 *   • Empty / zero states dim but don't disappear.
 *
 * No vendor-specific code. Generic for any EV tenant/scope.
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import {
    AlertTriangle, CheckCircle2, ChevronRight, ArrowUpRight, RefreshCw,
    BookmarkCheck, MessageSquare, MailOpen, Database, Activity,
    Sparkles, Building2, BadgeAlert, Loader2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

/* ─────────────────────── KPI Hero strip ─────────────────────── */
function KpiCell({ kpi }) {
    const isWarn = kpi.tone === "warn";
    return (
        <div
            className={`flex flex-col justify-between p-5 border-r border-border last:border-r-0 ${isWarn ? "bg-red-50/40 dark:bg-red-500/[0.04]" : ""}`}
            data-testid={`ev-hero-kpi-${kpi.key}`}
        >
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">
                    {kpi.label}
                </span>
                {isWarn && <BadgeAlert className="h-3.5 w-3.5 text-red-600 dark:text-red-400" strokeWidth={1.5} />}
            </div>
            <div className="mt-3 flex items-baseline gap-1">
                <span className={`text-4xl font-medium tracking-tight tabular-nums ${isWarn ? "text-red-700 dark:text-red-300" : ""}`}>
                    {kpi.value}
                </span>
                {kpi.unit && (
                    <span className="text-lg text-muted-foreground font-medium">{kpi.unit}</span>
                )}
            </div>
            {kpi.hint && (
                <p className="mt-2 text-[11px] text-muted-foreground leading-snug">
                    {kpi.hint}
                </p>
            )}
        </div>
    );
}

/* ─────────────────────── Scope chip / picker ────────────────── */
function ScopeBar({ scope, onScopeChange }) {
    if (!scope) return null;
    // Natural EV — read-only chip.
    if (!scope.cross_scope) {
        const parts = [];
        if (scope.current.org_name) parts.push(scope.current.org_name);
        if (scope.current.tenant_name) parts.push(scope.current.tenant_name);
        return (
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-muted-foreground" data-testid="ev-scope-chip">
                <Building2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                <span>{parts.join(" · ") || "Your scope"}</span>
            </div>
        );
    }
    // Cross-scope — minimal native selects (no shadcn complexity in this MVP).
    return (
        <div className="flex items-center gap-2" data-testid="ev-scope-picker">
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
            {scope.options.orgs.length > 0 && (
                <select
                    className="h-7 rounded-sm border border-border bg-card px-2 text-[11px] font-mono uppercase"
                    value={scope.current.org_id || ""}
                    onChange={(e) => onScopeChange({ org_id: e.target.value || null, tenant_id: null })}
                    data-testid="ev-scope-org-select"
                >
                    <option value="">All orgs</option>
                    {scope.options.orgs.map((o) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                </select>
            )}
            {scope.options.tenants.length > 0 && (
                <select
                    className="h-7 rounded-sm border border-border bg-card px-2 text-[11px] font-mono uppercase"
                    value={scope.current.tenant_id || ""}
                    onChange={(e) => onScopeChange({ tenant_id: e.target.value || null })}
                    data-testid="ev-scope-tenant-select"
                >
                    <option value="">All tenants</option>
                    {scope.options.tenants.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
            )}
        </div>
    );
}

/* ─────────────────────── Action tile (left column) ──────────── */
const ACTION_ICONS = {
    pending_shares:   MailOpen,
    unread_ai:        MessageSquare,
    owned_failed:     Database,
    stale_bookmarks:  BookmarkCheck,
};

function ActionTile({ item, onOpen }) {
    const Icon = ACTION_ICONS[item.key] || AlertTriangle;
    const zero = (item.count || 0) === 0;
    const warn = item.tone === "warn" && !zero;
    return (
        <button
            type="button"
            onClick={() => onOpen(item)}
            className={`text-left border rounded-sm border-l-2 p-4 transition-colors flex items-start gap-3 w-full ${
                zero
                    ? "border-border border-l-border bg-card/60 opacity-60 hover:opacity-100"
                    : warn
                        ? "border-border border-l-red-600 bg-red-50/40 dark:bg-red-500/[0.04] hover:bg-red-50/80 dark:hover:bg-red-500/[0.08]"
                        : "border-border border-l-foreground/60 bg-card hover:bg-secondary/40"
            }`}
            data-testid={`ev-action-tile-${item.key}`}
            data-state={zero ? "zero" : "active"}
        >
            <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${zero ? "text-muted-foreground" : warn ? "text-red-700 dark:text-red-300" : "text-foreground"}`} strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                    <span className={`text-2xl font-medium tabular-nums leading-none ${zero ? "text-muted-foreground" : warn ? "text-red-700 dark:text-red-300" : ""}`}>
                        {item.count}
                    </span>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                        {item.cta_label}
                        <ArrowUpRight className="h-3 w-3" />
                    </span>
                </div>
                <div className="mt-1.5 text-sm font-medium leading-tight">{item.label}</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground leading-snug">
                    {item.hint}
                </div>
            </div>
        </button>
    );
}

/* ─────────────────────── Info list rows (right column) ──────── */
function InfoListSection({ title, eyebrow, items, renderRow, emptyHint, testid }) {
    return (
        <div className="border border-border rounded-sm" data-testid={testid}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">{eyebrow}</div>
                    <h4 className="text-sm font-medium mt-0.5">{title}</h4>
                </div>
            </div>
            <div className="divide-y divide-border">
                {(items || []).length === 0 ? (
                    <div className="px-4 py-6 text-center text-[12px] text-muted-foreground">
                        {emptyHint}
                    </div>
                ) : (
                    items.map((it, i) => renderRow(it, i))
                )}
            </div>
        </div>
    );
}

function relTime(iso) {
    if (!iso) return "—";
    try {
        return formatDistanceToNow(new Date(iso), { addSuffix: true });
    } catch {
        return "—";
    }
}

function StatusDot({ status }) {
    const ok = ["ok", "success", "complete", "synced"].includes((status || "").toLowerCase());
    const warn = ["warning", "stale", "pending"].includes((status || "").toLowerCase());
    const bad = ["failed", "error"].includes((status || "").toLowerCase());
    const cls = bad ? "bg-red-600" : warn ? "bg-amber-500" : ok ? "bg-emerald-500" : "bg-muted-foreground";
    return <span className={`inline-block h-2 w-2 rounded-full ${cls}`} aria-hidden="true" />;
}

/* ─────────────────────── Main widget ────────────────────────── */
export default function EvCockpit() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scope, setScope] = useState(null);
    const [scopeOverride, setScopeOverride] = useState({});  // {org_id, tenant_id}

    const fetchAll = useCallback(async () => {
        try {
            const qs = new URLSearchParams();
            if (scopeOverride.tenant_id) qs.set("tenant_id", scopeOverride.tenant_id);
            if (scopeOverride.org_id) qs.set("org_id", scopeOverride.org_id);
            const [r1, r2] = await Promise.all([
                api.get(`/home/ev/cockpit${qs.toString() ? `?${qs.toString()}` : ""}`),
                api.get("/home/ev/scope"),
            ]);
            setData(r1.data || null);
            setScope(r2.data || null);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [scopeOverride]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const handleAction = (item) => {
        if (item.route) navigate(item.route);
    };

    if (loading) {
        return (
            <div className="space-y-4" data-testid="ev-cockpit-loading">
                <div className="h-32 border border-border rounded-sm shimmer" />
                <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-12 lg:col-span-5 space-y-3">
                        {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 border border-border rounded-sm shimmer" />)}
                    </div>
                    <div className="col-span-12 lg:col-span-7 space-y-3">
                        {[1, 2, 3, 4].map((i) => <div key={i} className="h-28 border border-border rounded-sm shimmer" />)}
                    </div>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="border border-border rounded-sm p-8 text-center text-sm text-muted-foreground" data-testid="ev-cockpit-empty">
                Cockpit data is unavailable. Try refreshing the page.
            </div>
        );
    }

    const allClear = (data.total_action_count || 0) === 0;

    return (
        <div className="space-y-6" data-testid="ev-cockpit">
            {/* ── Scope bar — sticky chip / picker ── */}
            <div className="flex items-center justify-between gap-3 border-b border-border pb-3">
                <ScopeBar scope={scope} onScopeChange={(patch) => setScopeOverride((p) => ({ ...p, ...patch }))} />
                <button
                    type="button"
                    onClick={fetchAll}
                    className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="ev-cockpit-refresh"
                    title="Refresh"
                >
                    <RefreshCw className="h-3 w-3" strokeWidth={1.5} /> Refresh
                </button>
            </div>

            {/* ── Hero KPI strip (3 cells, flat-bordered) ── */}
            <div className="border border-border rounded-sm grid grid-cols-1 md:grid-cols-3" data-testid="ev-hero-strip">
                {(data.hero_kpis || []).map((k) => <KpiCell key={k.key} kpi={k} />)}
            </div>

            {/* ── 5/7 asymmetric split ── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* For Your Action — left, 5 cols */}
                <section className="lg:col-span-5 space-y-3" data-testid="ev-action-column">
                    <header className="flex items-baseline justify-between">
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">For Your Action</div>
                            <h3 className="text-lg font-medium tracking-tight mt-0.5">
                                {allClear ? "You're all caught up" : "What needs your attention"}
                            </h3>
                        </div>
                        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                            {data.total_action_count} {data.total_action_count === 1 ? "signal" : "signals"}
                        </span>
                    </header>
                    {allClear ? (
                        <div className="border border-emerald-300 dark:border-emerald-500/30 rounded-sm bg-emerald-50/40 dark:bg-emerald-500/[0.04] p-6 flex items-start gap-3" data-testid="ev-action-all-clear">
                            <CheckCircle2 className="h-5 w-5 text-emerald-700 dark:text-emerald-400 shrink-0 mt-0.5" strokeWidth={1.5} />
                            <div>
                                <div className="text-sm font-medium text-emerald-900 dark:text-emerald-200">Nothing pending</div>
                                <p className="mt-1 text-[12px] text-muted-foreground leading-relaxed">
                                    No shares to accept, no failed datasets, no unread Sense AI replies, no stale bookmarks. Go explore a dashboard.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {(data.actions || []).map((it) => (
                                <ActionTile key={it.key} item={it} onOpen={handleAction} />
                            ))}
                        </div>
                    )}
                </section>

                {/* For Your Information — right, 7 cols */}
                <section className="lg:col-span-7 space-y-4" data-testid="ev-info-column">
                    <header>
                        <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">For Your Information</div>
                        <h3 className="text-lg font-medium tracking-tight mt-0.5">What&apos;s happening in your scope</h3>
                    </header>

                    {/* Top dashboards */}
                    <InfoListSection
                        eyebrow="Most viewed · 30d"
                        title="Top dashboards"
                        items={data.info?.top_dashboards}
                        emptyHint="No dashboards in your scope yet."
                        testid="ev-info-top-dashboards"
                        renderRow={(d, i) => (
                            <button
                                key={d.id || i}
                                type="button"
                                onClick={() => navigate(`/dashboards/${d.id || ""}`)}
                                className="flex items-center justify-between px-4 py-2.5 hover:bg-secondary/40 w-full text-left transition-colors"
                                data-testid={`ev-info-dashboard-${i}`}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <span className="text-[10px] font-mono text-muted-foreground tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-sm font-medium truncate">{d.name}</span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-[11px] font-mono tabular-nums text-muted-foreground">{d.view_count} views</span>
                                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                                </div>
                            </button>
                        )}
                    />

                    {/* Refresh status */}
                    <InfoListSection
                        eyebrow="Last refresh"
                        title="Dataset freshness"
                        items={data.info?.refresh_status}
                        emptyHint="No datasets visible in your scope."
                        testid="ev-info-refresh"
                        renderRow={(d, i) => (
                            <div
                                key={d.id || i}
                                className="flex items-center justify-between px-4 py-2.5"
                                data-testid={`ev-info-refresh-${i}`}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <StatusDot status={d.status} />
                                    <span className="text-sm truncate">{d.name}</span>
                                </div>
                                <span className="text-[11px] font-mono text-muted-foreground shrink-0">
                                    {relTime(d.last_refresh_at)}
                                </span>
                            </div>
                        )}
                    />

                    {/* Recent activity + What's new — two-column on lg */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <InfoListSection
                            eyebrow="In your scope"
                            title="Recent activity"
                            items={data.info?.recent_activity}
                            emptyHint="No recent activity."
                            testid="ev-info-activity"
                            renderRow={(a, i) => (
                                <div
                                    key={i}
                                    className="flex items-start gap-3 px-4 py-2.5"
                                    data-testid={`ev-info-activity-${i}`}
                                >
                                    <Activity className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" strokeWidth={1.5} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[12px] leading-snug">
                                            <span className="font-medium">{a.actor}</span>
                                            <span className="text-muted-foreground"> · {a.action}</span>
                                        </div>
                                        <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                                            {relTime(a.ts)}
                                        </div>
                                    </div>
                                </div>
                            )}
                        />
                        <InfoListSection
                            eyebrow="Platform"
                            title="What's new"
                            items={data.info?.whats_new}
                            emptyHint="No release notes yet."
                            testid="ev-info-whatsnew"
                            renderRow={(p, i) => (
                                <div
                                    key={i}
                                    className="px-4 py-2.5"
                                    data-testid={`ev-info-whatsnew-${i}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <Sparkles className="h-3.5 w-3.5 text-foreground" strokeWidth={1.5} />
                                        <span className="text-sm font-medium leading-tight">{p.title}</span>
                                        {p.version && (
                                            <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{p.version}</span>
                                        )}
                                    </div>
                                    {p.summary && (
                                        <p className="mt-1 text-[11px] text-muted-foreground leading-relaxed line-clamp-2">{p.summary}</p>
                                    )}
                                    {p.published_at && (
                                        <div className="text-[10px] font-mono text-muted-foreground mt-1">{relTime(p.published_at)}</div>
                                    )}
                                </div>
                            )}
                        />
                    </div>
                </section>
            </div>
        </div>
    );
}
